import Link from "next/link";
import { TemplateItem } from "@plura/shared/lead-forms";
import { cn, convertToTitleCase } from "@plura/shared/utils";

interface Props {
  className?: string;
  template: TemplateItem;
}

function ArrowRight({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden className={className}>
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Landing-app template card — links-only, styled after the home page article
 * card (borderless cover → meta → title → divider → animated "view" link).
 */
const TemplateCard = ({ className, template }: Props) => {
  const { id, title, description, image, tags, popular } = template;

  return (
    <Link href={`/templates/${id}`} className={cn("group flex flex-col gap-4 text-inherit no-underline", className)}>
      <div className="relative overflow-hidden rounded-2xl">
        {popular && (
          <span className="bg-primary absolute left-3 top-3 z-10 inline-flex items-center rounded-full px-3 py-1 font-body text-[11px] font-bold uppercase tracking-[0.04em] text-white">
            Popular
          </span>
        )}
        <img
          className="h-auto w-full transition-transform duration-300 group-hover:scale-[1.03]"
          src={`${image}?q=70&w=900`}
          alt={title}
        />
      </div>

      <div className="flex flex-1 flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          {/* Meta — primary tag */}
          <div className="text-dark/60 flex items-center gap-3 font-body text-xs font-bold uppercase tracking-[0.03em]">
            {tags.length > 0 && <span>{convertToTitleCase(tags[0])}</span>}
          </div>

          <h3 className="font-body line-clamp-2 text-[22px] leading-[30px] font-medium text-dark transition-colors duration-200 group-hover:text-primary">
            {title}
          </h3>

          <p className="font-body line-clamp-2 text-[15px] leading-relaxed text-[#555555]">{description}</p>
        </div>

        {/* Footer — divider + view link */}
        <div className="mt-auto flex flex-col gap-2.5">
          <div className="border-dark/15 border-t" />
          <div className="flex items-center justify-between text-dark">
            <span className="font-body text-[16px] font-bold">View Template</span>
            <span className="arrow-loop size-4" aria-hidden>
              <ArrowRight />
              <ArrowRight />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default TemplateCard;
